import NiceModal, { NiceModalHocProps } from "@ebay/nice-modal-react";
import { Divider, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { ApiClientAxios as Axios } from "@repo/api";
import { InfiniteData, UseInfiniteQueryResult } from "@repo/logic";
import { FC, useCallback } from "react";
import { NumberToCZK } from "../../helpers/NumberToCzFormatter";
import { formatDate } from "../../helpers/StringFormatter";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { InfoIconModalConfirm } from "../InfoIconModalConfirm";
import { InternalLink } from "../InternalLink";
import { ModalSlideUp } from "../Modals/ModalSlideUp";
import { ModalWith2Buttons, ModalWith2ButtonsProps } from "../Modals/ModalWith2Buttons";
import { BodyTextM, BodyTextS, HeadingM } from "../Typography";
import * as Styled from "./TransactionsTab.styled";
import { TransactionsTabPaymentsOverviewFilterModal } from "./TransactionsTabPaymentsOverviewFilterModal";
import { useContributionTypeFormatter, usePaymentTypeFormatter } from "./TransactionTab.helpers";

type TransactionsTabPaymentsOverviewProps = {
    moneyInsQuery?: {
        query: UseInfiniteQueryResult<InfiniteData<Axios.FufiFinancialInfoResponse, unknown>, unknown>;
        flatData: Axios.FufiFinancialInfoMoneyIn[];
    };
    filter?: PaymentsOverviewFilter;
    onFilterChange?: (filter: PaymentsOverviewFilter) => void;
};

export type PaymentsOverviewFilter = {
    from: Date | null;
    to: Date | null;
    moneyInsPayerTypes: Axios.FufiContributionType[] | null;
    moneyInsPaymentTypes: Axios.FufiPaymentType[] | null;
};

const initialFilterState: PaymentsOverviewFilter = {
    from: null,
    to: null,
    moneyInsPayerTypes: [],
    moneyInsPaymentTypes: [],
};

const LoadingComponent: FC<{ isLoading?: boolean }> = ({ isLoading }) => (
    <>
        {isLoading && (
            <TableRow>
                <TableCell colSpan={7}>
                    <Skeleton width="100%" />
                </TableCell>
            </TableRow>
        )}
    </>
);

export const TransactionsTabPaymentsOverview: FC<TransactionsTabPaymentsOverviewProps> = ({
    moneyInsQuery,
    filter,
    onFilterChange,
}) => {
    const { t } = useTypedTranslation();
    const isDesktop = useIsDesktop();
    const { flatData: data, query } = moneyInsQuery || {};
    const { fetchNextPage, hasNextPage, isLoading } = query || {};
    const formatPaymentType = usePaymentTypeFormatter();
    const formatContributionType = useContributionTypeFormatter();

    const handleShowMore = useCallback(() => {
        if (hasNextPage) fetchNextPage?.();
    }, [fetchNextPage, hasNextPage]);

    const handleShowfilter = useCallback(
        async (ev: React.SyntheticEvent) => {
            ev.preventDefault();
            const result = await NiceModal.show<
                false | null | PaymentsOverviewFilter,
                ModalWith2ButtonsProps & NiceModalHocProps,
                ModalWith2ButtonsProps
            >(ModalWith2Buttons, {
                width: 480,
                button1Text: t("fufi.paymentFilters.cancel"),
                button2Text: t("fufi.paymentFilters.accept"),
                title: t("fufi.paymentFilters.title"),
                resetReturnPayloadOnClose: true,
                content: ({ setReturnPayload }) => (
                    <TransactionsTabPaymentsOverviewFilterModal
                        setReturnPayload={setReturnPayload}
                        initialValues={filter ?? initialFilterState}
                    />
                ),
                components: {
                    DialogContentWrapperComponent: Styled.FilterDialogContent,
                },
            });

            if (typeof result === "object" && result !== null) {
                onFilterChange?.({
                    from: result.from,
                    to: result.to,
                    moneyInsPayerTypes: result.moneyInsPayerTypes,
                    moneyInsPaymentTypes: result.moneyInsPaymentTypes,
                });
            } else if (result === false) {
                onFilterChange?.({ ...initialFilterState });
            }
        },
        [filter, onFilterChange, t],
    );

    const handleShowDetail = useCallback(
        (payment: Axios.FufiFinancialInfoMoneyIn) => {
            NiceModal.show(ModalSlideUp, {
                title: t("fufi.paymentOverviewTable.titles.detailTitle"),
                content: (
                    <Styled.StackWithGap divider={<Divider />}>
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.paymentOverviewTable.titles.type")}</BodyTextS>
                            <BodyTextS>{formatPaymentType(payment.type)}</BodyTextS>
                        </Styled.StackSplitted>
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.paymentOverviewTable.titles.payer")}</BodyTextS>
                            <BodyTextS>{formatContributionType(payment.contractContributionType)}</BodyTextS>
                        </Styled.StackSplitted>
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.paymentOverviewTable.titles.amount")}</BodyTextS>
                            <BodyTextS>{`${NumberToCZK(payment.amount)}`}</BodyTextS>
                        </Styled.StackSplitted>
                        {!!payment.refunded && (
                            <Styled.StackSplitted>
                                <BodyTextS>{t("fufi.paymentOverviewTable.titles.refundedTitle")}</BodyTextS>
                                <BodyTextS>{`${t("fufi.paymentOverviewTable.titles.refunded")} ${formatDate(payment.date, "-")} ${t("fufi.paymentOverviewTable.titles.refundedSuffix")} ${NumberToCZK(payment.refunded)}.`}</BodyTextS>
                            </Styled.StackSplitted>
                        )}
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.paymentOverviewTable.titles.remainingToUse")}</BodyTextS>
                            <BodyTextS>{`${NumberToCZK(payment.balance)}`}</BodyTextS>
                        </Styled.StackSplitted>
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.paymentOverviewTable.titles.date")}</BodyTextS>
                            <BodyTextS>{formatDate(payment.date, "")}</BodyTextS>
                        </Styled.StackSplitted>
                    </Styled.StackWithGap>
                ),
            });
        },
        [formatContributionType, formatPaymentType, t],
    );

    return (
        <Stack>
            <Styled.StackBetween>
                <HeadingM>{t("fufi.paymentOverview.title")}</HeadingM>
                <InternalLink href="/" onClick={handleShowfilter}>
                    {t("fufi.paymentFilters.filter")}
                </InternalLink>
            </Styled.StackBetween>

            {isDesktop ? (
                <Table>
                    <TableHead>
                        <TableCell>{t("fufi.paymentOverviewTable.titles.type")}</TableCell>
                        <TableCell>{t("fufi.paymentOverviewTable.titles.payer")}</TableCell>
                        <Styled.WrappedTableCell $size={200}>
                            {t("fufi.paymentOverviewTable.titles.amount")}
                        </Styled.WrappedTableCell>
                        <Styled.WrappedTableCell $size={200}>
                            {t("fufi.paymentOverviewTable.titles.remainingToUse")}
                        </Styled.WrappedTableCell>
                        <Styled.WrappedTableCell $size={150}>
                            {t("fufi.paymentOverviewTable.titles.date")}
                        </Styled.WrappedTableCell>
                    </TableHead>
                    <TableBody>
                        {data?.map((payment) => (
                            <TableRow key={JSON.stringify(payment)}>
                                <Styled.LeftTableCell>{formatPaymentType(payment.type)}</Styled.LeftTableCell>
                                <Styled.LeftTableCell>
                                    {formatContributionType(payment.contractContributionType)}
                                </Styled.LeftTableCell>
                                <Styled.RightTableCell>
                                    <Styled.StackRowEndAligned>
                                        {!!payment.refunded && (
                                            <InfoIconModalConfirm
                                                title={t("fufi.paymentOverviewTable.titles.refundedTitle")}
                                                content={`${t("fufi.paymentOverviewTable.titles.refunded")} ${formatDate(payment.date, "-")} ${t("fufi.paymentOverviewTable.titles.refundedSuffix")} ${NumberToCZK(payment.refunded)}.`}
                                            />
                                        )}
                                        {`${NumberToCZK(payment.amount)}`}
                                    </Styled.StackRowEndAligned>
                                </Styled.RightTableCell>
                                <Styled.RightTableCell>{`${NumberToCZK(payment.balance)}`}</Styled.RightTableCell>
                                <Styled.RightTableCell>{`${formatDate(payment.date, "-")}`}</Styled.RightTableCell>
                            </TableRow>
                        ))}
                        {data?.length === 0 && !isLoading && (
                            <BodyTextM>{t("life.transactions.noTransactions")}</BodyTextM>
                        )}
                        <LoadingComponent isLoading={isLoading} />
                    </TableBody>
                </Table>
            ) : (
                <Table>
                    <TableHead>
                        <TableCell>{t("fufi.paymentOverviewTable.titles.date")}</TableCell>
                        <TableCell>{t("fufi.paymentOverviewTable.titles.type")}</TableCell>
                        <TableCell> </TableCell>
                    </TableHead>
                    <TableBody>
                        {data?.map((payment) => (
                            <TableRow key={JSON.stringify(payment)} onClick={() => handleShowDetail(payment)}>
                                <TableCell>{`${formatDate(payment.date, "")}`}</TableCell>
                                <TableCell>{formatPaymentType(payment.type)}</TableCell>
                                <TableCell>
                                    <Styled.SelectArrowOrange />
                                </TableCell>
                            </TableRow>
                        ))}
                        <LoadingComponent isLoading={isLoading} />
                    </TableBody>
                </Table>
            )}

            <Styled.StackBetween>
                <Styled.TransactionTriggerArrow onClick={handleShowMore} disabled={hasNextPage === false}>
                    {t("fufi.paymentOverview.olderPayments")}
                </Styled.TransactionTriggerArrow>
            </Styled.StackBetween>
        </Stack>
    );
};
